import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';
import { App } from '@capacitor/app';
import { ModalController, NavController } from '@ionic/angular';
import { PlatformService } from '@services/local/platform/platform.service';
import { defer, filter, merge, Subject, switchMap } from 'rxjs';

type NavigationOptions = NonNullable<Parameters<NavController['navigateRoot']>[1]>;

export type NavigationParams = {
    url: string | UrlTree | any[];
    options?: NavigationOptions;
};

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    private navController = inject(NavController);
    private modalController = inject(ModalController);
    private platformService = inject(PlatformService);
    private router = inject(Router);
    appBarBackButtonClickedSubject = new Subject<boolean>();

    previousPage: NavigationParams | null = null;
    currentPage: NavigationParams | null = null;

    navigate(url: string | UrlTree | any[], options?: NavigationOptions): Promise<boolean> {
        //not want to add back button arrow when user is on the homepage and doesnt have any previous page
        if (this.currentPage === null && url.toString().includes('home')) this.navController.navigateRoot(url);

        //saving current and previous page alway when page is not different than previous or when previous pages are empty
        if (
            (this.currentPage === null || this.currentPage !== this.previousPage) &&
            (!this.currentPage?.url.toString().includes(url.toString()) || this.currentPage?.options !== options)
        ) {
            this.previousPage = this.currentPage;
            this.currentPage = { url: url, options: options };
        }
        if (options) return this.navController.navigateRoot(url, options);

        return this.navController.navigateRoot(url);
    }

    displayBackButton() {
        if (this.platformService.isMobile() && this.platformService.isWeb() && !this.router.url.includes('/home') && (!this.currentPage || !this.previousPage))
            return true;

        if (!this.platformService.isMobile()) return false;
        else if (!this.currentPage || !this.previousPage) return false;
        else if (this.previousPage.url?.toString().includes('login')) return false;

        return true;
    }

    backButtonClicked$ = merge(this.platformService.backButtonClicked(), this.appBarBackButtonClickedSubject.asObservable()).pipe(
        takeUntilDestroyed(),
        filter(() => {
            return this.platformService.isMobile();
        }),
        switchMap(() => defer(() => this.dissmisModal()))
    );

    initializeBackButtonAction() {
        this.backButtonClicked$.subscribe(() => {
            if (
                this.platformService.isMobile() &&
                this.platformService.isWeb() &&
                !this.router.url.includes('/home') &&
                (!this.currentPage || !this.previousPage)
            ) {
                this.navController.navigateRoot(['/home']);
            } else if (!this.currentPage || !this.previousPage) App.exitApp();
            else if (this.previousPage.url?.toString().includes('login')) {
                App.exitApp();
            } else if (this.previousPage.url?.toString().includes('home')) {
                this.currentPage = this.previousPage;
                this.previousPage = null;
                this.navController.navigateRoot(this.currentPage!.url, this.currentPage?.options);
            } else {
                this.currentPage = this.previousPage;
                this.previousPage = { url: ['/home'], options: undefined };
                this.navController.navigateRoot(this.currentPage!.url, this.currentPage?.options);
            }
        });

        window.addEventListener('popstate', async () => {
            await this.dissmisModal();
        });
    }

    async dissmisModal() {
        const modal = await this.modalController.getTop();
        if (modal) modal.dismiss();
    }
}
